import { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ApiKeyGate } from "@/components/ApiKeyGate";
import { AppShell } from "@/components/AppShell";
import { api, getApiKey, getToken, getUser, setApiKey, setSession } from "@/lib/api";
import { MODES, ModeContext } from "@/lib/mode";
import HomePage from "@/pages/HomePage";
import JobsPage from "@/pages/JobsPage";
import ResultPage from "@/pages/ResultPage";
import SettingsPage from "@/pages/SettingsPage";
import DocsPage from "@/pages/DocsPage";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import AccountPage from "@/pages/AccountPage";
import "@/App.css";

/** Customer portal: MagicAd by DigiHome. Requires a logged-in user (JWT). */
function CustomerApp() {
  const [user, setUser] = useState(getUser());
  const [checked, setChecked] = useState(false);
  const loc = useLocation();
  useEffect(() => {
    if (!getToken()) { setChecked(true); return; }
    api.get("/auth/me").then((r) => { setUser(r.data.user); setSession(getToken(), r.data.user); }).catch(() => { setSession(null, null); setUser(null); }).finally(() => setChecked(true));
  }, []);
  const logout = useCallback(() => { setSession(null, null); setUser(null); }, []);
  if (!checked) return <div className="min-h-screen bg-background" />;
  if (!getToken() || !user) return <Navigate to={`/logg-inn?next=${encodeURIComponent(loc.pathname)}`} replace />;
  return (
    <ModeContext.Provider value={MODES.customer}>
      <AppShell user={user} onLogout={logout}>
        <Routes>
          <Route index element={<HomePage />} />
          <Route path="annonser" element={<JobsPage />} />
          <Route path="annonser/:jobId" element={<ResultPage />} />
          <Route path="konto" element={<AccountPage user={user} onUser={setUser} onLogout={logout} />} />
          <Route path="*" element={<Navigate to="/app" replace />} />
        </Routes>
      </AppShell>
    </ModeContext.Provider>
  );
}

/** Internal Lab: service key, technical view, API docs. */
function LabApp() {
  const [unlocked, setUnlocked] = useState(Boolean(getApiKey()));
  const [keyInfo, setKeyInfo] = useState(null);
  useEffect(() => {
    if (!getApiKey()) return;
    api.post("/lab/auth/verify").then((r) => { setKeyInfo(r.data); setUnlocked(true); }).catch(() => { setApiKey(""); setUnlocked(false); });
  }, []);
  const logout = useCallback(() => { setApiKey(""); setUnlocked(false); setKeyInfo(null); }, []);
  if (!unlocked) return <ApiKeyGate onUnlocked={(info) => { setKeyInfo(info); setUnlocked(true); }} />;
  return (
    <ModeContext.Provider value={MODES.lab}>
      <AppShell keyInfo={keyInfo} onLogout={logout}>
        <Routes>
          <Route index element={<HomePage />} />
          <Route path="jobs" element={<JobsPage />} />
          <Route path="jobs/:jobId" element={<ResultPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="docs" element={<DocsPage />} />
          <Route path="*" element={<Navigate to="/lab" replace />} />
        </Routes>
      </AppShell>
    </ModeContext.Provider>
  );
}

function App() {
  return (
    <div className="App">
      <TooltipProvider delayDuration={200}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/logg-inn" element={<AuthPage mode="login" />} />
            <Route path="/registrer" element={<AuthPage mode="register" />} />
            <Route path="/app/*" element={<CustomerApp />} />
            <Route path="/lab/*" element={<LabApp />} />
            {/* old Lab links */}
            <Route path="/jobs" element={<Navigate to="/lab/jobs" replace />} />
            <Route path="/jobs/:jobId" element={<LegacyJobRedirect />} />
            <Route path="/settings" element={<Navigate to="/lab/settings" replace />} />
            <Route path="/docs" element={<Navigate to="/lab/docs" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors={false} closeButton toastOptions={{ classNames: { toast: "rounded-2xl border bg-card text-foreground shadow-lg", title: "font-semibold", description: "text-muted-foreground" } }} />
      </TooltipProvider>
    </div>
  );
}

function LegacyJobRedirect() {
  const loc = useLocation();
  return <Navigate to={loc.pathname.replace(/^\/jobs/, "/lab/jobs")} replace />;
}

export default App;
